import React from 'react';
import { motion } from 'framer-motion';
import { StyleSheet, css } from 'aphrodite';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { isValidPhoneNumber } from 'libphonenumber-js';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/material.css';

const validationSchema = Yup.object().shape({
  name: Yup.string().required('Name is required'),
  relationship: Yup.string().required('Relationship is required'),
  phoneNumber: Yup.string()
    .required('Phone number is required')
    .test('is-valid-phone', 'Invalid phone number', (value) =>
      isValidPhoneNumber(value, 'KE')
    ),
  email: Yup.string().email('Invalid email')
});

const optionalValidationSchema = Yup.object().shape({
  name: Yup.string(),
  relationship: Yup.string(),
  phoneNumber: Yup.string().test(
    'is-valid-phone',
    'Invalid phone number',
    (value) => isValidPhoneNumber(value, 'KE')
  ),
  email: Yup.string().email('Invalid email')
});

const EmergencyContact = ({ initialData, onComplete, onBack, optional }) => {
  return (
    <motion.div
      className={css(styles.container)}
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <h2 className={css(styles.title)}>Emergency Contact</h2>
      <Formik
        initialValues={{
				  name: initialData.name || '',
				  relationship: initialData.relationship || '',
				  phoneNumber: initialData.phoneNumber || '',
				  email: initialData.email || ''
        }}
        validationSchema={
					!optional ? validationSchema : optionalValidationSchema
				}
        onSubmit={(values, { setSubmitting }) => {
				  onComplete(values, true);
				  setSubmitting(false);
        }}
      >
        {({ errors, touched, isValid, dirty }) => (
          <Form className={css(styles.form)}>
            <div className={css(styles.fieldGroup)}>
              <label htmlFor='name' className={css(styles.label)}>
                Name
              </label>
              <Field name='name' className={css(styles.input)} />
              {errors.name && touched.name && (
                <div className={css(styles.error)}>{errors.name}</div>
              )}
            </div>

            <div className={css(styles.fieldGroup)}>
              <label htmlFor='relationship' className={css(styles.label)}>
                Relationship
              </label>
              <Field
                name='relationship'
                as='select'
                className={css(styles.input)}
              >
                <option value=''>Select Relationship</option>
                <option value='spouse'>Spouse</option>
                <option value='parent'>Parent</option>
                <option value='sibling'>Sibling</option>
                <option value='friend'>Friend</option>
                <option value='other'>Other</option>
              </Field>
              {errors.relationship && touched.relationship && (
                <div className={css(styles.error)}>{errors.relationship}</div>
              )}
            </div>

            <div className={css(styles.fieldGroup)}>
              <label htmlFor='phoneNumber' className={css(styles.label)}>
                Phone Number
              </label>
              <Field name='phoneNumber'>
                {({ field, form }) => (
                  <PhoneInput
                    {...field}
                    country='ke'
                    onlyCountries={['ke']}
                    inputProps={{
										  name: 'phoneNumber'
                    // required: true,
                    }}
                    onChange={(value) =>
										  form.setFieldValue('phoneNumber', '+' + value)}
                    dropdownClass={css(styles.phoneDropdownCustom)}
                    inputClass={css(styles.phoneInput, styles.phoneInputCustom)}
                  />
                )}
              </Field>
              {errors.phoneNumber && touched.phoneNumber && (
                <div className={css(styles.error)}>{errors.phoneNumber}</div>
              )}
            </div>

            <div className={css(styles.fieldGroup)}>
              <label htmlFor='email' className={css(styles.label)}>
                Email (Optional)
              </label>
              <Field name='email' type='email' className={css(styles.input)} />
              {errors.email && touched.email && (
                <div className={css(styles.error)}>{errors.email}</div>
              )}
            </div>
            <div className={css(styles.buttonContainer)}>
              <motion.button
                type='submit'
                className={css(
								  styles.button,
								  !(isValid && dirty) && styles.disabledButton
                )}
                whileHover={isValid && dirty ? { scale: 1.05 } : {}}
                whileTap={isValid && dirty ? { scale: 0.95 } : {}}
                disabled={!(isValid && dirty)}
              >
                {!optional ? 'Next' : 'Update Profile'}
              </motion.button>
              {!optional
                ? (
                  <motion.button
                    type='button'
                    className={css(styles.button, styles.backButton)}
                    onClick={onBack}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Back
                  </motion.button>
                  )
                : null}
            </div>
          </Form>
        )}
      </Formik>
    </motion.div>
  );
};

const styles = StyleSheet.create({
  container: {
    marginBottom: '2rem'
  },
  title: {
    fontSize: '1.5rem',
    marginBottom: '1rem',
    color: '#2783F7'
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem'
  },
  fieldGroup: {
    display: 'flex',
    flexDirection: 'column'
  },
  label: {
    marginBottom: '0.5rem',
    fontSize: '1rem',
    color: '#4a5568'
  },
  input: {
    padding: '0.5rem',
    fontSize: '1rem',
    borderRadius: '5px',
    border: '1px solid #e2e8f0'
  },
  error: {
    color: 'red',
    fontSize: '0.875rem',
    marginTop: '0.25rem'
  },
  buttonContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    marginTop: '1rem'
  },
  button: {
    backgroundColor: '#2783F7',
    color: 'white',
    border: 'none',
    padding: '0.5rem 1rem',
    fontSize: '1rem',
    borderRadius: '5px',
    cursor: 'pointer',
    marginTop: '1rem'
  },
  disabledButton: {
    opacity: 0.5,
    cursor: 'not-allowed'
  },
  phoneInput: {
    width: '100%',
    padding: '0.5rem',
    paddingLeft: '50px',

    fontSize: '1rem',
    borderRadius: '5px',
    border: '1px solid #e2e8f0'
  },
  phoneInputCustom: {
    '.form-control': {
      paddingLeft: '50px'
    }
  },
  phoneDropdownCustom: {
    color: 'black'
  },
  backButton: {
    backgroundColor: '#e2e8f0',
    color: '#4a5568'
  }
});

export default EmergencyContact;
